// Sugar Alarm — cycle 435: the Live Activity and home-screen widget are
// seeded THROUGH the selector, never from the raw reading.
//
// ⚠ WHY THIS FILE EXISTS. `App.tsx` used to seed the iOS Live Activity and
// both widgets from `socket.lastReading`, judged by the reading's own
// timestamp against `Date.now()` and nothing else: no stream confirmation,
// no server verdict. The widget is a separate process, but it is FED from
// here, so it is not exempt — a value the app could not prove current was
// still written to the lock screen as if it were. This module is the one
// place a native glance payload is built, and the only input it accepts
// for the number is a `CurrentGlucoseView`.
//
// DISPLAY ONLY (the cycle-73 invariant): nothing here touches the alarm
// engine, and the payload's `stale` bit is a glance-tile rendering hint,
// never a rail predicate. Pure, so the seed rule is pinned under node.

import type { CurrentGlucoseView } from "./currentGlucose";
import { recentDeltaMgdl, zoneOf } from "./glucose";
import type { LiveActivitySeed } from "./liveActivity";
import type { Reading, Thresholds } from "./types";

/** How long past a stale sample the Live Activity is ENDED rather than
 *  frozen (the server's stale-date horizon). Unchanged from cycle 168. */
export const LIVE_ACTIVITY_END_AFTER_MS = 35 * 60_000;

export interface NativeGlanceContext {
  memberId: string;
  memberName: string;
  units: "mg/dL" | "mmol/L";
  thresholds: Thresholds;
  /** History for the widget's delta and sparkline — never the number. */
  recentReadings: readonly Reading[];
}

export interface NativeGlanceDecision {
  /** The Live Activity should be ended, not refreshed: the last sample is
   *  stale past the tile window (or there is no sample at all). */
  endLiveActivity: boolean;
  /** The payload to hand `ensureLiveActivity` / `writeGlucoseWidgetSnapshot`
   *  when not ending. `mgdl` is null and `stale` true whenever the selector
   *  did not release a current value. */
  seed: LiveActivitySeed;
}

/** Build the native glance seed from the selector's result. The NUMBER
 *  comes from `view.displayValue` and nowhere else; the end rule reads the
 *  raw sample's age (a reading the selector withheld for being unconfirmed
 *  may still be minutes old, and the tile should keep its "stale" face
 *  rather than vanish). `nowMs` is sampled by the caller. */
export function nativeGlanceSeed(
  view: CurrentGlucoseView,
  ctx: NativeGlanceContext,
  nowMs: number,
): NativeGlanceDecision {
  const current = view.displayValue;
  const stale = current == null;
  const sampleTsMs = view.reading ? new Date(view.reading.timestamp).getTime() : NaN;
  const tsMs = Number.isFinite(sampleTsMs) ? sampleTsMs : null;
  const endLiveActivity =
    stale && (tsMs == null || nowMs - tsMs > LIVE_ACTIVITY_END_AFTER_MS);

  const recent = ctx.recentReadings;
  // The delta and the sparkline are history, and only ride a CURRENT
  // number — a withheld number with a live delta beside it would still
  // read as a live tile.
  const deltaMgdl = current != null ? recentDeltaMgdl(recent) : null;
  const points = current != null ? recent.slice(-12).map((r) => r.sgv) : [];

  return {
    endLiveActivity,
    seed: {
      memberId: ctx.memberId,
      memberName: ctx.memberName,
      units: ctx.units,
      mgdl: current?.sgv ?? null,
      trend: current?.trend ?? null,
      zone: current ? zoneOf(current.sgv, ctx.thresholds) : "in_range",
      stale,
      tsMs: current ? current.timestampMs : tsMs,
      deltaMgdl,
      recent: points,
    },
  };
}
